/**
 * Transform assignment data for Timeline chart
 */

import dayjs from 'dayjs'

import {
    OncallAssignmentResponse,
    OncallSwapResponse,
} from '../../../../types/response'
import {
    TimelineBarItem,
    LayeredAssignments,
    EmployeeLegendItem,
    ScheduleGroup,
} from '../../../../types/timeline'
// Atlassian Opsgenie-style color palette for employee bars
const ATLASSIAN_COLORS = [
    '#00B8D9', // Teal
    '#6554C0', // Purple
    '#00875A', // Green
    '#0052CC', // Blue
    '#FF5630', // Red
    '#FFAB00', // Yellow
    '#36B37E', // Light Green
    '#403294', // Dark Purple
    '#008DA6', // Dark Teal
    '#172B4D', // Dark Blue
]

// Layer colors - only Base (scheduled) and Override (swaps)
export const LAYER_COLORS = {
    base: '#00B8D9', // Teal for scheduled assignments
    override: '#FFAB00', // Amber/Yellow for swaps/overrides
}

// Swap status colors
export const SWAP_STATUS_COLORS: Record<string, string> = {
    pending: '#FFAB00', // Amber - waiting for response
    approved: '#00875A', // Green - approved and executed
    rejected: '#FF5630', // Red - rejected
    cancelled: '#97A0AF', // Gray - cancelled
}

/**
 * Get color for swap status
 */
export const getSwapStatusColor = (status: string): string => {
    return SWAP_STATUS_COLORS[status] || SWAP_STATUS_COLORS.pending
}

// Layer to row index mapping (only base and override)
const LAYER_INDEX: Record<'base' | 'override', number> = {
    base: 0,
    override: 1,
}

/**
 * Get consistent color for an employee based on their ID
 * Uses Atlassian-style colors for consistency
 */
export const getEmployeeColor = (employeeId: number): string => {
    return ATLASSIAN_COLORS[employeeId % ATLASSIAN_COLORS.length]
}

/**
 * Group assignments by layer (Base for scheduled)
 * - Base: Original scheduled assignments
 * Note: Override layer is now populated by transformSwapsToTimelineData
 */
export const groupAssignmentsByLayer = (
    assignments: OncallAssignmentResponse[]
): LayeredAssignments => {
    const layered: LayeredAssignments = {
        final: [], // Not used, kept for type compatibility
        override: [], // Populated by swaps in createTimelineDataset
        base: [],
    }

    assignments.forEach((assignment) => {
        // Base layer shows all scheduled assignments
        layered.base.push(assignment)
    })

    return layered
}

/**
 * Transform assignments to timeline chart data format
 */
export const transformToTimelineData = (
    assignments: OncallAssignmentResponse[],
    layer: 'base' | 'override' = 'base'
): TimelineBarItem[] => {
    return assignments.map((assignment) => {
        const startTime = dayjs(assignment.oncall_start_time).valueOf()
        const endTime = dayjs(assignment.oncall_end_time).valueOf()

        return {
            name: assignment.employee_name,
            value: [LAYER_INDEX[layer], startTime, endTime, endTime],
            employeeId: assignment.employee_id,
            employeeName: assignment.employee_name,
            assignmentId: assignment.oncall_assignment_id,
            scheduleId: assignment.oncall_schedule_id,
            scheduleName: assignment.oncall_schedule_name || '',
            layer,
            status: assignment.oncall_assignment_status,
            itemStyle: {
                color: getEmployeeColor(assignment.employee_id),
            },
            createdBy: assignment.created_by,
            createdByName: assignment.created_by_name,
        }
    })
}

/**
 * Transform swaps to timeline chart data format for the override layer
 * Shows pending and approved swaps with the TARGET employee (who will take over)
 */
export const transformSwapsToTimelineData = (
    swaps: OncallSwapResponse[]
): TimelineBarItem[] => {
    // Only show pending and approved swaps
    const visibleSwaps = swaps.filter((swap) =>
        ['pending', 'approved'].includes(swap.oncall_swap_status)
    )

    return visibleSwaps.map((swap) => {
        // Use the actual start/end times from the swap response
        const startTime = dayjs(swap.oncall_start_time).valueOf()
        const endTime = dayjs(swap.oncall_end_time).valueOf()

        return {
            name: swap.target_name,
            value: [LAYER_INDEX.override, startTime, endTime, endTime],
            employeeId: swap.target_employee_id,
            employeeName: swap.target_name,
            assignmentId: swap.oncall_assignment_id,
            scheduleId: swap.oncall_schedule_id,
            scheduleName: swap.oncall_schedule_name || '',
            layer: 'override' as const,
            status: swap.oncall_swap_status,
            itemStyle: {
                color: getSwapStatusColor(swap.oncall_swap_status),
            },
            createdBy: null, // Swaps don't have created_by (created by requester)
            createdByName: swap.requester_name, // Use requester as creator
            // Swap-specific fields
            swapId: swap.oncall_swap_id,
            swapStatus: swap.oncall_swap_status,
            swapReason: swap.swap_reason || undefined,
            requesterEmployeeId: swap.requester_employee_id,
            requesterName: swap.requester_name,
        }
    })
}

/**
 * Combine layers into single dataset for chart (only base and override)
 */
export const createTimelineDataset = (
    layered: LayeredAssignments,
    swaps: OncallSwapResponse[] = []
): TimelineBarItem[] => {
    const allData: TimelineBarItem[] = []

    // Add Base layer data (scheduled assignments)
    allData.push(...transformToTimelineData(layered.base, 'base'))

    // Add Override layer data from swaps (Phase 2)
    allData.push(...transformSwapsToTimelineData(swaps))

    return allData
}

/**
 * Extract unique employees from assignments for legend
 */
export const extractUniqueEmployees = (
    assignments: OncallAssignmentResponse[]
): EmployeeLegendItem[] => {
    const employeeMap = new Map<number, EmployeeLegendItem>()

    assignments.forEach((assignment) => {
        if (!employeeMap.has(assignment.employee_id)) {
            employeeMap.set(assignment.employee_id, {
                employeeId: assignment.employee_id,
                employeeName: assignment.employee_name,
                color: getEmployeeColor(assignment.employee_id),
            })
        }

        // Also include original employee if reassigned
        if (
            assignment.original_employee_id &&
            !employeeMap.has(assignment.original_employee_id)
        ) {
            employeeMap.set(assignment.original_employee_id, {
                employeeId: assignment.original_employee_id,
                employeeName: assignment.original_employee_name || '',
                color: getEmployeeColor(assignment.original_employee_id),
            })
        }
    })

    return Array.from(employeeMap.values())
}

/**
 * Group assignments by schedule for multi-schedule timeline view
 * Returns an array of ScheduleGroup, each containing assignments for that schedule
 */
export const groupAssignmentsBySchedule = (
    assignments: OncallAssignmentResponse[],
    swaps: OncallSwapResponse[] = []
): ScheduleGroup[] => {
    // Group assignments by schedule_id
    const scheduleMap = new Map<
        number,
        {
            scheduleId: number
            scheduleName: string
            assignments: OncallAssignmentResponse[]
        }
    >()

    assignments.forEach((assignment) => {
        const scheduleId = assignment.oncall_schedule_id
        if (!scheduleMap.has(scheduleId)) {
            scheduleMap.set(scheduleId, {
                scheduleId,
                scheduleName:
                    assignment.oncall_schedule_name || `Schedule ${scheduleId}`,
                assignments: [],
            })
        }
        scheduleMap.get(scheduleId)!.assignments.push(assignment)
    })

    // Group swaps by schedule
    const swapsBySchedule = new Map<number, OncallSwapResponse[]>()
    swaps.forEach((swap) => {
        if (!swapsBySchedule.has(swap.oncall_schedule_id)) {
            swapsBySchedule.set(swap.oncall_schedule_id, [])
        }
        swapsBySchedule.get(swap.oncall_schedule_id)!.push(swap)
    })

    // Transform each schedule group
    const scheduleGroups: ScheduleGroup[] = []

    scheduleMap.forEach((group) => {
        const scheduleSwaps = swapsBySchedule.get(group.scheduleId) || []
        const layered = groupAssignmentsByLayer(group.assignments)
        const timelineData = createTimelineDataset(layered, scheduleSwaps)

        scheduleGroups.push({
            scheduleId: group.scheduleId,
            scheduleName: group.scheduleName,
            assignments: timelineData,
            isExpanded: false,
        })
    })

    // Sort by schedule name
    scheduleGroups.sort((a, b) => a.scheduleName.localeCompare(b.scheduleName))

    return scheduleGroups
}
